import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, Circle, X, Sparkle, ArrowRight, Confetti, MagicWand } from "@phosphor-icons/react";
import { toast } from "sonner";
import client from "../lib/api";
import GuidedSetupWizard, { shouldShowGuidedWizard } from "../components/GuidedSetupWizard";

/**
 * Persistent onboarding checklist that shows on the Dashboard for new users.
 * Auto-detects step completion from backend (based on actual data — no local state trickery).
 * Dismisses permanently when: (a) all steps done + user hits Celebrate, OR (b) user explicitly dismisses.
 */
export default function OnboardingChecklist({ onDismiss }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);

  const reload = async () => {
    try {
      const { data } = await client.get("/onboarding/status");
      setStatus(data);
      // Auto-open the guided wizard for fresh accounts.
      if (shouldShowGuidedWizard(data)) {
        setWizardOpen(true);
      }
    } catch (e) {
      // fail silent — widget just hides
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const dismiss = async () => {
    try {
      await client.post("/onboarding/dismiss");
      // Immediately hide the widget locally — don't wait for parent to refetch
      setStatus((prev) => (prev ? { ...prev, dismissed: true, show_widget: false } : prev));
      onDismiss?.();
      toast.success("Checklist hidden.");
    } catch (e) {
      toast.error("Could not dismiss");
    }
  };

  if (loading) return null;
  if (!status) return null;
  // Hide if user dismissed. Otherwise render (progress state OR all_done celebration).
  if (status.dismissed) return null;
  // If there are no steps configured at all, don't render.
  if (!status.steps || status.steps.length === 0) return null;

  const { steps = [], completed = 0, total = 5, progress_pct = 0, all_done } = status;

  return (
    <div
      className="relative rounded-2xl border border-[#FF5A1F]/30 bg-gradient-to-br from-[#FF5A1F]/[0.08] via-[#0F0F0F] to-[#0F0F0F] p-6 mb-6"
      data-testid="onboarding-checklist"
    >
      {/* Dismiss */}
      <button
        onClick={dismiss}
        title="Hide this checklist"
        data-testid="onboarding-dismiss-btn"
        className="absolute top-4 right-4 text-neutral-500 hover:text-white transition-colors"
      >
        <X size={18} />
      </button>

      {/* Header */}
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#FF5A1F] font-semibold mb-2">
        <Sparkle size={12} weight="fill" /> {all_done ? "You're all set!" : "Day 1 — Get Started"}
      </div>
      <h2 className="text-xl font-display font-semibold text-white tracking-tight">
        {all_done
          ? "You just activated FreightPilot 🎯"
          : "Set up your account in 3 minutes"}
      </h2>
      <p className="text-sm text-neutral-400 mt-1 max-w-2xl">
        {all_done
          ? "Every core feature is now ready. Time to save 8+ hours a week on ops."
          : `Complete ${total - completed} more step${total - completed === 1 ? "" : "s"} to unlock the full FreightPilot experience.`}
      </p>

      {/* Progress bar */}
      <div className="mt-4">
        <div className="flex items-center justify-between text-xs mb-1.5">
          <span className="text-neutral-400 font-medium">
            {completed} of {total} complete
          </span>
          <span className="text-[#FF5A1F] font-semibold">{progress_pct}%</span>
        </div>
        <div className="h-2 rounded-full bg-white/[0.05] overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#FF5A1F] to-[#FF8C4C] rounded-full transition-all duration-500"
            style={{ width: `${progress_pct}%` }}
          />
        </div>
      </div>

      {/* Steps */}
      <div className="mt-5 space-y-2">
        {!all_done && (
          <button
            onClick={() => setWizardOpen(true)}
            data-testid="onboarding-open-wizard-btn"
            className="w-full mb-2 rounded-lg py-3 px-4 border border-[#FF5A1F]/40 bg-[#FF5A1F]/[0.08] hover:bg-[#FF5A1F]/[0.14] transition-colors flex items-center justify-between gap-3 group"
          >
            <div className="flex items-center gap-3">
              <MagicWand size={18} weight="fill" className="text-[#FF9764] shrink-0" />
              <div className="text-left">
                <div className="text-sm font-semibold text-white">Open guided setup</div>
                <div className="text-[11px] text-neutral-400">
                  Add your first load + driver + connect email — one modal, 90 seconds
                </div>
              </div>
            </div>
            <ArrowRight size={14} weight="bold" className="text-[#FF9764] shrink-0 group-hover:translate-x-0.5 transition-transform" />
          </button>
        )}
        {steps.map((s, i) => (
          <Link
            key={s.key}
            to={s.action_url}
            data-testid={`onboarding-step-${s.key}`}
            className={`flex items-center gap-3 rounded-lg px-3 py-3 transition-all group ${
              s.done
                ? "bg-emerald-500/[0.06] border border-emerald-500/20"
                : "bg-white/[0.02] border border-white/10 hover:border-[#FF5A1F]/40 hover:bg-[#FF5A1F]/[0.05]"
            }`}
          >
            {s.done ? (
              <CheckCircle size={20} weight="fill" className="text-emerald-400 shrink-0" />
            ) : (
              <Circle size={20} className="text-neutral-600 shrink-0 group-hover:text-[#FF5A1F] transition-colors" />
            )}
            <div className="flex-1 min-w-0">
              <div
                className={`text-sm font-medium ${
                  s.done ? "text-neutral-500 line-through" : "text-white"
                }`}
              >
                {i + 1}. {s.label}
              </div>
              {!s.done && s.hint && (
                <div className="text-[11px] text-neutral-500 mt-0.5 leading-snug">{s.hint}</div>
              )}
            </div>
            {!s.done && (
              <div className="shrink-0 text-neutral-500 group-hover:text-[#FF5A1F] transition-colors inline-flex items-center gap-1 text-xs">
                Go <ArrowRight size={12} weight="bold" />
              </div>
            )}
          </Link>
        ))}
      </div>

      {all_done && (
        <button
          onClick={dismiss}
          data-testid="onboarding-celebrate-btn"
          className="mt-5 w-full rounded-full py-3 text-sm font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] transition-colors inline-flex items-center justify-center gap-2"
        >
          <Confetti size={14} weight="fill" /> Awesome — hide this checklist
        </button>
      )}
      <GuidedSetupWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onCompleted={() => { setWizardOpen(false); reload(); }}
      />
    </div>
  );
}
